/**
 * I TORNEI DI BACKGAMMON NELLA LOBBY — la parte pura: il filtro, il torneo in evidenza, e
 * «il tuo incontro». Reference `2026-09-23-backgammon-lobby.md` (approvata da Luca il 23/09).
 *
 * ═══ 🔑 COME SI SA CHE UN TORNEO E' DI BACKGAMMON ═══
 * Il torneo porta un `gameId` e nient'altro sul gioco. La corrispondenza si fa lato client:
 * `gameId` → `GET /v1/games` → `profileType === 'backgammon'`, la stessa giunzione che
 * `LimuloMatchDetail.tsx` usa per il profilo di gioco. La stringa e' verificata alla fonte
 * (`app-qgammon/manifest.js`, `PROFILE_TYPE`) [T qgammon-orc-1-ui, 23/09; il puntamento al
 * catalogo di staging, gioco 100 dalle 16:51Z, e' [R qgammon-orc], non misurato qui].
 * Decisione di `tma-orc` (23/09 18:02Z): un FILTRO e' un'altra domanda rispetto al
 * «passthrough» di kb#81, che vale per MOSTRARE il gioco e resta com'e'.
 *
 * ═══ 🔴 IL VUOTO CHE NON SI PUO' AFFERMARE ═══
 * Se `/v1/games` non risponde, **o** risponde e nessun gioco ha quel `profileType`, la lista
 * filtrata e' vuota per costruzione — e un vuoto prodotto dal filtro non e' «nessun torneo»:
 * e' il caso 4 della regola dei cinque vuoti (*non posso chiedere*), non il 5. Per questo il
 * risultato ha DUE forme e non una lista che puo' essere vuota per due ragioni.
 * ⛔ Nessun ripiego sul «primo gioco» o su un `gameId` cablato: funzionerebbe finche' il
 * catalogo ha un solo gioco, e sbaglierebbe il giorno che ne ha due, senza sintomi.
 */
import type { LimuloGame, LimuloMatch, LimuloMatchParticipant, LimuloTournament } from '@/types/limulo';
import { matchStateOf } from '@/lib/limulo/match';

/** Il `profileType` del backgammon nel catalogo giochi. Vedi il cappello. */
export const BACKGAMMON_PROFILE_TYPE = 'backgammon';

/**
 * I `gameId` del catalogo che sono backgammon, oppure `null` se il catalogo non c'e' o non ne
 * ha nessuno. ⚠️ `null` copre due fatti diversi (catalogo assente / senza quel gioco), e sta
 * bene: per chi legge sono la stessa risposta — *non posso dire quali tornei sono di
 * backgammon*.
 */
export function backgammonGameIdsOf(games: readonly LimuloGame[] | null | undefined): Set<string> | null {
  return gameIdsDelTipoOf(games, BACKGAMMON_PROFILE_TYPE);
}

/**
 * Lo STESSO filtro per un gioco qualunque, dato il suo `profileType` (E6 di ADR-0193, P2: la lobby degli scacchi chiede le
 * stesse cose per un altro tipo). `backgammonGameIdsOf` e' il caso del backgammon, e dice la stessa cosa di prima.
 */
export function gameIdsDelTipoOf(
  games: readonly LimuloGame[] | null | undefined,
  profileType: string,
): Set<string> | null {
  if (!Array.isArray(games)) return null;
  const ids = games
    .filter((g) => g.profileType === profileType && g.gameId !== '')
    .map((g) => g.gameId);
  return ids.length > 0 ? new Set(ids) : null;
}

export type BackgammonFilter =
  /** Non posso dire quali tornei sono di backgammon: il caso 4, mai un «nessun torneo». */
  | { kind: 'no-catalog' }
  /** Ho il catalogo: `tournaments` puo' essere vuoto, e in quel caso e' il caso 5. */
  | { kind: 'ok'; tournaments: LimuloTournament[] };

export function backgammonTournamentsOf(
  tournaments: readonly LimuloTournament[],
  games: readonly LimuloGame[] | null | undefined,
): BackgammonFilter {
  return torneiDelTipoOf(tournaments, games, BACKGAMMON_PROFILE_TYPE);
}

/** Come `backgammonTournamentsOf`, per un gioco qualunque dato il suo `profileType` (v. `gameIdsDelTipoOf`). */
export function torneiDelTipoOf(
  tournaments: readonly LimuloTournament[],
  games: readonly LimuloGame[] | null | undefined,
  profileType: string,
): BackgammonFilter {
  const ids = gameIdsDelTipoOf(games, profileType);
  if (ids === null) return { kind: 'no-catalog' };
  return { kind: 'ok', tournaments: tournaments.filter((t) => ids.has(t.gameId)) };
}

/**
 * Il torneo che la striscia mostra: fra quelli di backgammon, il primo **a cui sono iscritto**
 * e, se non lo sono a nessuno (o non lo so), il primo dell'elenco gia' ordinato.
 *
 * [D `tma-orc-27`] La reference dice «il torneo in forma compatta» al singolare e non dice
 * quale; questa e' la lettura piu' povera che regge: chi e' iscritto vede il SUO, e chi non lo
 * e' vede quello piu' vicino. ⚠️ `mineIds` `null` = «le mie iscrizioni non sono arrivate»:
 * non si deduce «non sei iscritto», si ripiega sul primo e basta.
 */
export function featuredTournamentOf(
  tournaments: readonly LimuloTournament[],
  mineIds: ReadonlySet<string> | null,
): LimuloTournament | null {
  if (tournaments.length === 0) return null;
  if (mineIds !== null) {
    const mio = tournaments.find((t) => mineIds.has(t.id) || mineIds.has(t.tournamentId));
    if (mio) return mio;
  }
  return tournaments[0];
}

export function isMineTournament(t: LimuloTournament, mineIds: ReadonlySet<string> | null): boolean {
  return mineIds !== null && (mineIds.has(t.id) || mineIds.has(t.tournamentId));
}

export interface OwnMatchRow {
  round: number | null;
  table: number | null;
  /** L'altra parte, o `null` se **non si sa quale lato sono io** (`isMe` non e' arrivato). */
  opponent: LimuloMatchParticipant | null;
}

/** Gli stati in cui l'incontro e' ancora «il tuo»: si gioca, o si aspetta l'esito del referto. */
const STATI_APERTI = new Set<string>(['pending', 'proposed', 'disputed']);

/**
 * «Il tuo incontro»: il primo (per round) fra i miei incontri non ancora conclusi, oppure `null`.
 *
 * ⚠️ `GET /v1/tournaments/:id/matches` restituisce **i miei** incontri, non il tabellone
 * (`emptyMatchesReasonOf`): per questo la lista e' gia' quella giusta e qui non si filtra per
 * utente. Restano fuori, di proposito [D `tma-orc-27`]: il bye (un turno passato, non un
 * incontro), lo slot ancora vuoto (`incomplete`, non c'e' nessuno da incontrare) e i conclusi.
 *
 * 🔴 L'avversario si ricava da `isMe` e **solo** da quello: un `isMe` che manca lascia
 * `opponent: null` — mai «l'altro lato» per esclusione, che con `isMe` assente sceglierebbe a
 * caso e attribuirebbe a una persona un incontro che non e' suo.
 */
export function ownMatchOf(matches: readonly LimuloMatch[] | null | undefined): OwnMatchRow | null {
  if (!Array.isArray(matches)) return null;
  const aperti = matches
    .filter((m) => STATI_APERTI.has(matchStateOf(m)))
    .sort((a, b) => (a.roundNumber ?? Infinity) - (b.roundNumber ?? Infinity));
  const m = aperti[0];
  if (!m) return null;
  const { a, b } = m.players;
  const opponent = a?.isMe === true ? b : b?.isMe === true ? a : null;
  return { round: m.roundNumber, table: m.tableNumber, opponent };
}
